import React from "react";
import { isProductBonusEnabledForCycle } from "@/features/productBonusFeature";
import { cn, getSeriesBatchItems, isProductEligibleForBonus, isProgrammedOrderItem } from "@/lib/utils";
import { calculateSeriesTotals, getPreviousYearPrice as getSeriesPreviousYearPrice } from "@/lib/seriesTotals";
import { getSecondSemesterAdjustmentDeadline } from "@/lib/orderCycle";
import { formatDate } from "@/lib/formatters";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight, Package, Users, Lock, Link, Info } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { QuantityStepper } from "../QuantityStepper";
import { ProductMigrationNotice, type ProductMigrationInfo } from "../ProductMigrationNotice";
interface OrderItem {
    id: number;
    codigo_produto: string;
    descricao_produto: string;
    preco: number;
    ano_letivo: number;
    marca: string;
    sku: string;
    status: string;
    utilizacao?: string | null;
    serie?: string | null;
    segmento?: string | null;
    quantidade_contratual: number;
    desconto: number;
    quantidade_pedido: number;
    quantidade_programada?: number;
    valor_liquido: number;
    valor_total: number;
    brand_id: number;
    adjustment: number;
    previousYearProductPrice?: number | null;
    product_migration?: ProductMigrationInfo | null;
    bonified_quantity?: number;
    available_bonus_quantity?: number;
    earned_bonus_quantity?: number;
    scheduling_category?: string | null;
    is_optional?: boolean;
}

interface SeriesCardProps {
    serie: string;
    items: OrderItem[];
    isOpen: boolean;
    onToggle: () => void;
    onUpdateQuantity: (itemId: number, quantity: number, updateAllInSeries?: boolean) => void;
    formatCurrency: (value: number) => string;
    clientHasOrders?: boolean;
    isScheduledArea?: boolean;
    isSimulated?: boolean;
    getDisplayQuantity?: (item: OrderItem) => number;
    cycleYear?: number;
}
export const SeriesCard = ({
    serie,
    items,
    isOpen,
    onToggle,
    onUpdateQuantity,
    formatCurrency,
    clientHasOrders = false,
    isScheduledArea,
    isSimulated,
    getDisplayQuantity,
    cycleYear = 2026,
}: SeriesCardProps) => {
    const isProductBonusEnabled = isProductBonusEnabledForCycle(cycleYear);
    // Helper local para obter quantidade de exibição
    const getItemDisplayQuantity = (item: OrderItem): number => {
        if (getDisplayQuantity) {
            return getDisplayQuantity(item);
        }

        return item.quantidade_pedido || 0;
    };
    // Item representativo para o cabeçalho: ignora itens que podem ser editados individualmente
    // (produto opcional só representa a série quando ela não tem produtos obrigatórios)
    const headerCandidates = getSeriesBatchItems(items);
    const representativeItem = headerCandidates.find(item =>
        String(item.utilizacao || "").toLowerCase() !== "professor" &&
        // !String(item.descricao_produto || "").toLowerCase().includes('1º semestre') &&
        item.quantidade_pedido > 0
    ) || headerCandidates.find(item =>
        String(item.utilizacao || "").toLowerCase() !== "professor" &&
        !String(item.descricao_produto || "").toLowerCase().includes('1º semestre')
    ) || items[0];

    // Check if this is professor material
    const isProfessorMaterial = items.some(item => String(item.utilizacao || "").toLowerCase() === "professor");

    // Materiais de professor com a feature de bonificação ativa para o ciclo: único ramo redesenhado (handoff SeriesCard 6a)
    const isBonusStyledSeries = isProfessorMaterial && isProductBonusEnabled;

    const firstItem = items[0];

    // Ano do ciclo atual e rótulo dinâmico do ano anterior
    const previousYear = cycleYear - 1;
    const previousYearLabel = `Preço por Aluno ${previousYear}`;

    // Preço do produto no ciclo anterior (null = campo oculto); regra em lib/seriesTotals.
    const getPreviousYearPrice = (item: OrderItem): number | null => getSeriesPreviousYearPrice(item, cycleYear);

    // Totais do cabeçalho (regra e motivo em lib/seriesTotals): na área cobrada agora os itens programados ficam de
    // fora; na área programada/simulada todos contam.
    const {
        seriesTotal,
        currentPrice: totalPreco2026,
        previousYearPrice: totalPreviousYearPrice,
    } = calculateSeriesTotals(items, cycleYear, !isScheduledArea && !isSimulated);
    const totalPreco2025 = totalPreviousYearPrice;

    // Debug logs
    // Check if series has products from restricted brands (AZ: 1, High Five: 2)
    const hasRestrictedBrandProducts = () => {
        return items.some(item => item.brand_id === 1 || item.brand_id === 2);
    };

    // Check if quantity can be updated (only allow Professor materials and optional products when expanded)
    const canUpdateQuantity = (item?: OrderItem) => {
        if (!isOpen) return true; // Always allow when collapsed
        if (item) {
            // Edição individual do 1º Semestre desativada; para reativar, restaurar a condição abaixo:
            // return item.utilizacao?.toLowerCase() === "professor" || item.descricao_produto.includes('1º Semestre');
            return item.utilizacao?.toLowerCase() === "professor" || !!item.is_optional;
        }
        return true;
    };
    const updateAllItems = (quantity: number) => {
        // Atualiza todos os itens do grupo em uma única chamada em lote,
        // evitando que múltiplas chamadas síncronas de onUpdateQuantity
        // sobrescrevam umas às outras usando o mesmo estado desatualizado.
        if (items.length > 0) {
            onUpdateQuantity(items[0].id, quantity, true);
        }
    };
    return <Card className="mb-4 hover:shadow-md">
        <Collapsible open={isOpen} onOpenChange={onToggle}>
            <CollapsibleTrigger asChild>
                <div className="p-6 cursor-pointer">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {!isScheduledArea && (
                                <div className="flex items-center gap-2">
                                    {isOpen ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                                </div>
                            )}
                            <div>
                                <div className="flex items-center gap-3 ">
                                    <Badge variant="outline" className="text-sm font-medium px-3 py-1">
                                        {serie}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            {representativeItem && <div className={isBonusStyledSeries ? "flex items-center gap-2.5" : "flex items-center gap-2"}>
                                {!isProfessorMaterial && !isSimulated && <>
                                    {!isScheduledArea ? (
                                        <>
                                            {totalPreviousYearPrice !== null && (
                                                <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                                                    <div className="text-[10px] text-gray-600 mb-0.5 font-medium">{previousYearLabel}</div>
                                                    <div className="font-semibold text-xs text-gray-900">
                                                        {formatCurrency(totalPreviousYearPrice)}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[60px]">
                                                <div className="text-[10px] text-gray-600 mb-0.5 font-medium leading-tight">
                                                    Qtd. Referência Aluno
                                                </div>
                                                <div className="font-semibold text-xs text-gray-900">
                                                    {representativeItem.quantidade_contratual}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[60px]">
                                                <div className="text-[10px] text-gray-600 mb-0.5 font-medium leading-tight">
                                                    Qtd. Programada
                                                </div>
                                                <div className="font-semibold text-xs text-gray-900">
                                                    {representativeItem.quantidade_contratual}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                                        <div className="text-[10px] text-gray-600 mb-0.5 font-medium">Preço por Aluno {cycleYear}</div>
                                        <div className="font-semibold text-xs text-gray-900">
                                            {formatCurrency(totalPreco2026)}
                                        </div>
                                    </div>
                                </>}
                                {isProfessorMaterial && isScheduledArea && (<>
                                    <>
                                        {totalPreviousYearPrice !== null && (
                                            <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[85px]">
                                                <div className="text-[10px] text-gray-600 mb-0.5 font-medium">{previousYearLabel}</div>
                                                <div className="font-semibold text-xs text-gray-900">
                                                    {formatCurrency(totalPreviousYearPrice)}
                                                </div>
                                            </div>
                                        )}

                                        <div className="px-2 py-1.5 bg-gray-100 rounded text-center min-w-[60px]">
                                            <div className="text-[10px] text-gray-600 mb-0.5 font-medium leading-tight">
                                                Qtd. Referência Aluno
                                            </div>
                                            <div className="font-semibold text-xs text-gray-900">
                                                {representativeItem.quantidade_contratual}
                                            </div>
                                        </div>
                                    </>
                                </>)}

                                {!isBonusStyledSeries && (
                                    <div className={`px-2 py-1.5 rounded text-center min-w-[105px] ${isSimulated ? 'bg-indigo-100' : isScheduledArea ? 'bg-purple-100' : 'bg-gray-100'}`}>
                                        <div className={`text-[10px] mb-0.5 font-medium leading-tight ${isSimulated ? 'text-indigo-600' : isScheduledArea ? 'text-purple-600' : 'text-gray-600'}`}>
                                            Qtd. Pedido
                                        </div>
                                        <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                                            <QuantityStepper
                                                size="sm"
                                                value={getItemDisplayQuantity(representativeItem)}
                                                productName={serie}
                                                onDecrement={() => updateAllItems(Math.max(getItemDisplayQuantity(representativeItem) - 1, 0))}
                                                onIncrement={() => updateAllItems(getItemDisplayQuantity(representativeItem) + 1)}
                                                onChange={value => updateAllItems(value)}
                                                valueClassName={isSimulated ? 'text-indigo-700' : isScheduledArea ? 'text-purple-700' : 'text-gray-900'}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>}

                            <div className="px-3 py-1.5 bg-green-50 rounded text-center min-w-[90px] ml-2">
                                <div className="text-sm font-bold text-green-600">
                                    {formatCurrency(seriesTotal)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CollapsibleTrigger>
            {!isScheduledArea && (<>
                <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                    <div className="px-6 pb-6 space-y-3">
                        <div className="border-t pt-4">
                            {isBonusStyledSeries && (
                                <div className="mb-[10px] grid grid-cols-[minmax(0,1fr)_95px_105px_135px_145px_130px] items-center gap-[4px] rounded-[10px] border border-[#e3e7ea] bg-muted/40 px-[22px] py-[10px] text-[11px] font-semibold leading-tight text-muted-foreground">
                                    <div>Produto</div>
                                    <div className="text-right">Preço {cycleYear}</div>
                                    <div className="text-center">Qtde bonificada<br /> total</div>
                                    <div className="text-center">Qtde do pedido</div>
                                    <div className="text-center">Saldo bonificado disponível</div>
                                    <div className="text-right">Total</div>
                                </div>
                            )}
                            {items.map(item => {
                                const price2025 = getPreviousYearPrice(item);
                                const isScheduledItem = item.descricao_produto.includes("Plataforma AZ") && item.descricao_produto.includes("2º Semestre");
                                const itemDisplayQuantity = getItemDisplayQuantity(item);
                                const itemBonusBalance = Math.max((item.available_bonus_quantity ?? 0) - itemDisplayQuantity, 0);
                                const isEligibleForBonus = isProductEligibleForBonus(item.descricao_produto);
                                const isOverBonusLimit = isBonusStyledSeries
                                    && isEligibleForBonus
                                    && itemDisplayQuantity > (item.available_bonus_quantity ?? 0);
                                return <div key={item.id} className={isBonusStyledSeries
                                    ? "mb-[10px] rounded-[10px] border border-[#e3e7ea] bg-card px-[22px] py-[18px]"
                                    : "p-4 rounded-lg border border-border bg-card mb-3"}>
                                    <div className={isBonusStyledSeries ? "flex items-center gap-[10px]" : "flex items-center justify-between"}>
                                        <div className={isBonusStyledSeries ? "min-w-0 flex-1" : "flex-1 min-w-0 pr-6"}>
                                            <div className={isBonusStyledSeries
                                                ? "text-[17px] font-semibold leading-snug text-foreground"
                                                : "font-medium text-foreground mb-2"}>
                                                {item.descricao_produto}
                                            </div>
                                            {cycleYear >= 2027 && isProgrammedOrderItem(item, cycleYear) && item.scheduling_category != "ExamScheduled" && item.scheduling_category != "ImmediateAndExam" && (
                                                <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                    <p className="text-xs text-blue-800">
                                                        Pedido programado 2º semestre. As quantidades poderão ser ajustadas até {formatDate(getSecondSemesterAdjustmentDeadline(cycleYear))}.
                                                    </p>
                                                </div>
                                            )}
                                            <ProductMigrationNotice info={item.product_migration} cycleYear={cycleYear} formatCurrency={formatCurrency} />
                                            {/* Info-box de programado: só mostra quando clientHasOrders=true, isScheduledItem=true, e NÃO está na área programada separada */}
                                            {/* {clientHasOrders && isScheduledItem && !isScheduledArea && !isSimulated && (
                                            <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-blue-800">Pedido programado 2º semestre. As quantidades poderão ser ajustadas até 10/04/2026.</p>
                                            </div>
                                        )} */}
                                            {/* {clientHasOrders && isScheduledItem && isScheduledArea && !isSimulated && (
                                            <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <p className="text-xs text-blue-800">Quantidade acumulada referente aos pedidos principais e complementares já Realizado de Contratos.</p>
                                            </div>
                                        )} */}
                                        </div>
                                        <div className={isBonusStyledSeries
                                            ? "grid grid-cols-[95px_105px_135px_145px_130px] items-center gap-[4px]"
                                            : "flex items-center gap-6"}>
                                            {isBonusStyledSeries ? (
                                                <>
                                                    {!isSimulated && (
                                                        <div className="text-right text-sm font-medium text-foreground">
                                                            {formatCurrency(item.preco)}
                                                        </div>
                                                    )}

                                                    <div className="flex justify-center">
                                                        {isEligibleForBonus ? (
                                                            <div className="flex h-[34px] min-w-[46px] items-center justify-center rounded-[8px] border border-dashed border-[#c3cad1] px-2 font-mono text-[15px] font-medium text-muted-foreground">
                                                                {item.earned_bonus_quantity ?? 0}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground" aria-label="Produto sem bonificação">—</span>
                                                        )}
                                                    </div>

                                                    <div className="flex justify-center">
                                                        <QuantityStepper
                                                            value={itemDisplayQuantity}
                                                            productName={item.descricao_produto}
                                                            disabled={!canUpdateQuantity(item)}
                                                            onDecrement={() => onUpdateQuantity(item.id, Math.max(itemDisplayQuantity - 1, 0))}
                                                            onIncrement={() => onUpdateQuantity(item.id, itemDisplayQuantity + 1)}
                                                            onChange={value => onUpdateQuantity(item.id, value)}
                                                        />
                                                    </div>

                                                    <div className="flex items-center justify-center gap-2">
                                                        {isEligibleForBonus ? (
                                                            <div
                                                                aria-live="polite"
                                                                className={cn(
                                                                    "flex h-[34px] min-w-[46px] items-center justify-center rounded-[8px] font-mono text-[15px] font-semibold",
                                                                    itemBonusBalance > 0
                                                                        ? "border border-green-100 bg-green-50/60 text-green-600"
                                                                        : "border border-[#e0e4e8] bg-muted text-muted-foreground",
                                                                )}
                                                            >
                                                                {itemBonusBalance}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground" aria-label="Produto sem saldo bonificado">—</span>
                                                        )}
                                                        {/* <span className={itemBonusBalance > 0 ? "text-[11.5px] text-green-700" : "text-[11.5px] text-muted-foreground"}>
                                                            {itemBonusBalance > 0 ? "para usar" : "esgotado"}
                                                        </span> */}
                                                    </div>

                                                    <div className="text-right">
                                                        <div className="text-[17px] font-bold text-foreground">
                                                            {formatCurrency(item.valor_total)}
                                                        </div>
                                                    </div>

                                                    {isOverBonusLimit && (
                                                        <p className="col-start-2 col-span-3 row-start-2 mt-1.5 text-left text-[10.5px] font-medium leading-tight text-info">
                                                            Você utilizou toda a quantidade bonificada disponível. As unidades adicionais serão cobradas no valor do pedido.
                                                        </p>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {isProfessorMaterial ? <>
                                                        {!isSimulated && (
                                                            <div className="text-right space-y-1 w-32">
                                                                <div className="text-xs text-muted-foreground">Preço {cycleYear ?? item.ano_letivo}</div>
                                                                <div className="font-medium text-primary text-sm">{formatCurrency(item.preco)}</div>
                                                            </div>
                                                        )}

                                                        {!isProductBonusEnabled && (
                                                            <div className="text-center w-20">
                                                                <div className="text-xs text-muted-foreground mb-1">Qtd. Referência Professor</div>
                                                                <Badge variant="outline" className="text-sm">
                                                                    {item.quantidade_contratual}
                                                                </Badge>
                                                            </div>
                                                        )}

                                                        {!isProductBonusEnabled && !isSimulated && (
                                                            <div className="text-center w-16">
                                                                <div className="text-xs text-muted-foreground mb-1">Desconto</div>
                                                                <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs px-2 py-1">
                                                                    {item.desconto}%
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </> : <>
                                                        {!isSimulated && <>
                                                            {!isScheduledArea && <>
                                                                {(() => {
                                                                    const prevPrice = getPreviousYearPrice(item);
                                                                    return prevPrice !== null ? (
                                                                        <div className="text-right space-y-1 w-32">
                                                                            <div className="text-xs text-muted-foreground">{previousYearLabel}</div>
                                                                            <div className="font-medium text-sm">{formatCurrency(prevPrice)}</div>
                                                                        </div>
                                                                    ) : null;
                                                                })()}

                                                                <div className="text-center w-20">
                                                                    <div className="text-xs text-muted-foreground mb-1">Qtd. Referência Aluno</div>
                                                                    <Badge variant="outline" className="text-sm">
                                                                        {item.quantidade_contratual}
                                                                    </Badge>
                                                                </div>

                                                                <div className="text-center w-16">
                                                                    <div className="text-xs text-muted-foreground mb-1">Reajuste</div>
                                                                    <Badge variant="secondary" className="bg-orange-400 text-white text-xs px-2 py-1">
                                                                        {item.adjustment.toFixed(2)}%
                                                                    </Badge>
                                                                </div>
                                                            </>}

                                                            <div className="text-right space-y-1 w-32">
                                                                <div className="text-xs text-muted-foreground">Preço por Aluno {cycleYear}</div>
                                                                <div className="font-medium text-primary text-sm">{formatCurrency(item.valor_liquido)}</div>
                                                            </div>
                                                        </>}
                                                    </>}

                                                    <div className="text-center">
                                                        <div className="text-xs text-muted-foreground mb-1">
                                                            {`Qtd. ${isScheduledArea ? 'Programada' : 'Pedido'}`}
                                                        </div>
                                                        <div className="flex justify-center">
                                                            <QuantityStepper
                                                                size="sm"
                                                                value={getItemDisplayQuantity(item)}
                                                                productName={item.descricao_produto}
                                                                disabled={!canUpdateQuantity(item)}
                                                                onDecrement={() => onUpdateQuantity(item.id, Math.max(getItemDisplayQuantity(item) - 1, 0))}
                                                                onIncrement={() => onUpdateQuantity(item.id, getItemDisplayQuantity(item) + 1)}
                                                                onChange={value => onUpdateQuantity(item.id, value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="text-right w-32">
                                                        <div className="text-xs text-muted-foreground mb-1">Total</div>
                                                        <div className="text-xl font-bold text-green-600">
                                                            {formatCurrency(item.valor_total)}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div >
                                    </div >
                                </div >;
                            })}
                        </div >
                    </div >
                </CollapsibleContent >
            </>)}

        </Collapsible >
    </Card >;
};
